import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

/**
 * Toast notification system
 * =========================
 *
 * Wrap the app in <ToastProvider> (already done in main.jsx / App.jsx) and use
 * the `useToast()` hook from any component:
 *
 *   const toast = useToast();
 *   toast.success('User added successfully');
 *   toast.error('Failed to save user');
 *   toast.info(...);
 *   toast.warning(...);
 *
 * Features:
 *   - Stacked toasts in bottom-right corner
 *   - Auto-dismiss after 4 seconds (configurable)
 *   - Manual close button
 *   - Success / error / warning / info variants with colour + icon
 *   - Smooth slide-in / fade-out animation
 *   - Accessible: role=status for info/success, role=alert for error/warning
 */

const ToastContext = createContext(null);

const VARIANTS = {
    success: {
        icon: CheckCircle,
        color: '#059669',
        bg: '#F0FDF4',
        border: 'rgba(5,150,105,0.25)',
        role: 'status',
    },
    error: {
        icon: XCircle,
        color: '#DC2626',
        bg: '#FEF2F2',
        border: 'rgba(220,38,38,0.25)',
        role: 'alert',
    },
    warning: {
        icon: AlertTriangle,
        color: '#D97706',
        bg: '#FFFBEB',
        border: 'rgba(217,119,6,0.25)',
        role: 'alert',
    },
    info: {
        icon: Info,
        color: '#0D9488',
        bg: '#F0FDFA',
        border: 'rgba(13,148,136,0.25)',
        role: 'status',
    },
};

const DEFAULT_DURATION = 4000;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback((variant, message, options = {}) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const duration = options.duration ?? DEFAULT_DURATION;
        const toast = {
            id,
            variant,
            message,
            title: options.title,
            duration,
            createdAt: Date.now(),
        };
        setToasts((prev) => [...prev, toast]);
        if (duration > 0) {
            setTimeout(() => dismiss(id), duration);
        }
        return id;
    }, [dismiss]);

    const toast = {
        success: (msg, opts) => show('success', msg, opts),
        error: (msg, opts) => show('error', msg, opts),
        warning: (msg, opts) => show('warning', msg, opts),
        info: (msg, opts) => show('info', msg, opts),
        dismiss,
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} onDismiss={dismiss} />
        </ToastContext.Provider>
    );
};

/**
 * useToast — access the toast API from any component.
 * Falls back to window.alert if the provider is not mounted, so code paths
 * that happen before the provider is ready (e.g. module-scope) still work.
 */
export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // Fallback used when ToastProvider isn't in the tree (e.g. during tests).
        // Logs to console instead of blocking with window.alert.
        return {
            success: (m) => console.info('[toast:success]', m),
            error: (m) => console.error('[toast:error]', m),
            warning: (m) => console.warn('[toast:warning]', m),
            info: (m) => console.info('[toast:info]', m),
            dismiss: () => { },
        };
    }
    return ctx;
};

/* ── Container ──────────────────────────────────────────── */
const ToastContainer = ({ toasts, onDismiss }) => {
    if (!toasts || toasts.length === 0) return null;
    return (
        <div
            aria-live="polite"
            aria-atomic="false"
            style={{
                position: 'fixed',
                zIndex: 999999,  
                bottom: 24,
                right: 24,
                //zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                maxWidth: 'calc(100vw - 48px)',
                width: 380,
                pointerEvents: 'none',
            }}
        >
            {toasts.map((t) => (
                <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
            ))}
        </div>
    );
};

/* ── Single toast ───────────────────────────────────────── */
const ToastItem = ({ toast, onDismiss }) => {
    const [exiting, setExiting] = useState(false);
    const meta = VARIANTS[toast.variant] || VARIANTS.info;
    const Icon = meta.icon;

    useEffect(() => {
        if (toast.duration > 0) {
            const t = setTimeout(() => setExiting(true), toast.duration - 280);
            return () => clearTimeout(t);
        }
    }, [toast.duration]);

    const handleClose = () => {
        setExiting(true);
        setTimeout(() => onDismiss(toast.id), 250);
    };

    return (
        <div
            role={meta.role}
            style={{
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 14,
                background: '#fff',
                border: `1px solid ${meta.border}`,
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(15, 23, 42, 0.06)',
                animation: exiting
                    ? 'toastOut 0.25s ease-in forwards'
                    : 'toastIn 0.3s cubic-bezier(0.2, 0.8, 0.2, 1) forwards',
                borderLeft: `4px solid ${meta.color}`,
            }}
        >
            <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: meta.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                <Icon size={17} color={meta.color} strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                {toast.title && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1D21', marginBottom: 2 }}>
                        {toast.title}
                    </div>
                )}
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {toast.message}
                </div>
            </div>
            <button
                onClick={handleClose}
                aria-label="Dismiss"
                style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: 'none', background: 'transparent',
                    color: '#9CA3AF', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, padding: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#4B5563'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9CA3AF'; }}
            >
                <X size={14} strokeWidth={2.5} />
            </button>

            {/* Inline keyframes — scoped here so callers don't need CSS edits */}
            <style>{`
        @keyframes toastIn {
          0% { opacity: 0; transform: translateX(100%) scale(0.95); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastOut {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(110%) scale(0.95); }
        }
      `}</style>
        </div>
    );
};

export default ToastProvider;
